import { RefetchQueriesInclude, useApolloClient } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import { Alert, Box, Button, Stack, Tooltip, useTheme } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid, IssueMessageCell } from "components/DataGrid";
import {
  AddEditDocumentDialogState,
  DocumentDialog,
  EditMode,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { FormState } from "components/Document/AddEditDocumentDialog/types";
import { hmbpSectionFromDocumentType } from "encamp-shared/src/hmbp";
import { gql } from "generated-graphql";
import {
  DocumentGridRowFragment,
  DocumentType,
  FacilityDocumentInput,
  Issue,
  TagInput,
} from "generated-graphql/graphql";
import { useDeleteDocumentMutation, useRemoveDocumentFromReportMutation } from "hooks/documents";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { PaginationModel } from "hooks/usePaginationModel";
import { DateTime } from "luxon";
import { useDocumentReview } from "providers/documentReview";
import { useMemo, useState } from "react";
import { CaliforniaDocumentTypes, TierIIDocumentTypes, documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

import { HmbpDocumentForm } from "../Chemicals/Report/StateInformation/hmbpComponents/HmbpDocumentForm";
import { useHmbpFeature } from "../Chemicals/Report/useHmbp";
import { useReport } from "../Chemicals/Report/useReport";
import { useFacility } from "../Facility/useFacility";

gql(`
  fragment DocumentGridRow on Document {
    id
    title
    fileExtension
    documentType
    storageLink
    currentSubmissionDocument
    createdAt
    authoredAt
    description
    sizeMegabytes
    associatedReports {
      reportId
      submissionId
      reportingYear
      programArea
      reportKind
    }
  }
`);

type DocumentIssueRow = Issue & GridValidRowModel;

export type DocumentRecommendationRow = {
  message: string;
  type: "recommendation";
  documentType?: DocumentType;
  id: string;
};

type DataGridRow = DocumentGridRowFragment | DocumentIssueRow;

const isDocumentIssueRow = (row: DataGridRow): row is DocumentIssueRow => {
  return (row as DocumentIssueRow).key !== undefined;
};

type RemoveDialogState = {
  open: boolean;
  message: string;
  documentIdToRemove: string;
  reportId: string;
  documentTitle?: string;
};

const defaultRemoveDialogState: RemoveDialogState = {
  open: false,
  message:
    "This action is permanent. This document may be associated with other facilities, reports and chemicals.",
  documentIdToRemove: "",
  reportId: "",
};

type DocumentTypeStrategy = "default" | "tier-ii";

type DocumentsGridProps = {
  loading: boolean;
  documents: DocumentGridRowFragment[];
  /** These are issues that will display their message as a row in the grid.
   * Used primarily when a facility is missing a specific document type. */
  facilityDocumentIssues: Issue[];
  tenantId: string | undefined;
  facility: FacilityDocumentInput | undefined;
  reportId?: string;
  refetchQueries: RefetchQueriesInclude;
  tags?: TagInput[];
  paginationModel?: PaginationModel;
  setPaginationModel?: (pagination: PaginationModel) => void;
  sortModel?: GridSortModel;
  setSortModel?: (sort: GridSortModel) => void;
  count?: number;
  deleteDocument: boolean;
  isReadOnly?: boolean;
  /** Strategy for determining which document types to show in the AddEditDocumentDialog.
   * - "default": Use the component's default document types or CA-specific document types (fallback behavior)
   * - "tier-ii": Use Tier II specific document types when not hazardous materials business plan (HMBP)
   */
  addEditDocumentTypeStrategy?: DocumentTypeStrategy;
};

export const DocumentsGrid: React.FC<DocumentsGridProps> = ({
  loading,
  documents,
  facilityDocumentIssues,
  tenantId,
  facility,
  reportId,
  refetchQueries,
  tags,
  paginationModel,
  setPaginationModel,
  sortModel,
  setSortModel,
  count,
  deleteDocument,
  isReadOnly = false,
  addEditDocumentTypeStrategy = "default",
}) => {
  // Need both the facility and the report since this component is used in
  // both contexts
  const fullFacility = useFacility();
  const report = useReport();
  const isHmbp = useHmbpFeature(
    fullFacility?.data?.facility?.state ?? report?.data?.tierIIReport?.facility?.state ?? ""
  );
  const { fetchAndHandleDownload } = useDocumentDownload();

  const theme = useTheme();
  const apolloClient = useApolloClient();
  const alerts = useAlerts();

  const selectableDocumentTypes = useMemo(() => {
    if (isHmbp) return CaliforniaDocumentTypes as DocumentType[];
    if (addEditDocumentTypeStrategy === "tier-ii") return TierIIDocumentTypes;
    return undefined;
  }, [isHmbp, addEditDocumentTypeStrategy]);

  const rows: DataGridRow[] = useMemo(
    () => [...documents, ...facilityDocumentIssues.map((i) => ({ ...i, id: i.message }))],
    [facilityDocumentIssues, documents]
  );

  const additionalAddEditState: Partial<AddEditDocumentDialogState> = useMemo(() => {
    return {
      facilities: facility ? [facility] : undefined,
      documentTags: tags,
    };
  }, [facility, tags]);

  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>({
    ...defaultAddEditDocumentDialogState,
  });
  const [removeDialogState, setRemoveDialogState] =
    useState<RemoveDialogState>(defaultRemoveDialogState);

  const { documentsToReview, setDocumentsToReview, clearDocumentsToReview } = useDocumentReview();
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });

  const [removeDocumentFromReport, { loading: removingDocument }] =
    useRemoveDocumentFromReportMutation({ refetchQueries });
  const [deleteDocumentMutation, { loading: deletingDocument }] = useDeleteDocumentMutation({
    refetchQueries,
  });

  const hasDocumentAI = useDocumentAI();

  const columns: GridColDef<DataGridRow>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Name",
        flex: 1.5,
        colSpan: (_, row) => {
          if (isDocumentIssueRow(row)) {
            return 3;
          }

          return undefined;
        },
        renderCell: ({ row }) => {
          if (isDocumentIssueRow(row)) {
            return <IssueMessageCell issue={row} />;
          }

          return (
            <div title={row.title} role="presentation">
              {row.title}
            </div>
          );
        },
      },
      {
        field: "fileExtension",
        headerName: "Format",
        flex: 0.5,
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.75,
        valueGetter: (_, row) => {
          if (!isDocumentIssueRow(row)) {
            return documentTypeToLabel(row.documentType);
          }

          return "";
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.5,
        valueFormatter: (_, row) => (row.createdAt ? prettyPrintDateMed(row.createdAt) : undefined),
      },
      {
        field: "actions",
        type: "actions",
        align: "right",
        flex: 0.4,
        getActions: ({ row }) => {
          if (isReadOnly) {
            return [
              <Tooltip title="Download" key="downloadDocument">
                <GridActionsCellItem
                  label="Download"
                  icon={<Download />}
                  onClick={async () =>
                    await fetchAndHandleDownload({
                      documentId: row.id,
                    })
                  }
                />
              </Tooltip>,
            ];
          }

          if (isDocumentIssueRow(row)) {
            return [
              <Button
                key="add"
                color="primary"
                size="small"
                sx={{ my: 1 }}
                startIcon={<Add fontSize="small" />}
                onClick={() => {
                  setAddEditDialogState((state) => ({
                    ...state,
                    open: true,
                    mode: EditMode.ADD_OR_PICK,
                    documentType: row.metadata?.documentType,
                    currentSubmissionDocument: true,
                    isAddingFromIssue: true,
                    authoredAt: DateTime.now().toISODate(),
                    ...additionalAddEditState,
                  }));
                }}
              >
                Add Document
              </Button>,
            ];
          }

          return [
            <Tooltip title="Edit" key="editRecord">
              <GridActionsCellItem
                onClick={() => {
                  if (!hasDocumentAI) {
                    // for non ai customers, use the add edit dialog
                    setAddEditDialogState((state) => ({
                      ...state,
                      ...row,
                      open: true,
                      mode: EditMode.EDIT,
                      step: FormState.FORM,
                      documentId: row.id,
                      currentSubmissionDocument: row.currentSubmissionDocument ?? false,
                      ...additionalAddEditState,
                    }));
                  } else {
                    // Use ReviewDocumentMetadataDialog for editing
                    setDocumentsToReview([row.id]);
                  }
                }}
                label="Edit"
                icon={<Edit />}
              />
            </Tooltip>,
            <Tooltip title="Download" key="downloadDocument">
              <GridActionsCellItem
                label="Download"
                icon={<Download />}
                onClick={async () =>
                  await fetchAndHandleDownload({
                    documentId: row.id,
                  })
                }
              />
            </Tooltip>,
            <Tooltip title={deleteDocument ? "Delete" : "Remove"} key="deleteRecord">
              <GridActionsCellItem
                onClick={() =>
                  setRemoveDialogState((state) => ({
                    ...state,
                    open: true,
                    message: deleteDocument
                      ? removeDialogState.message
                      : `Are you sure you want to remove ${row.title} from this report? This will not delete the document from your document catalog.`,
                    documentIdToRemove: row.id,
                    reportId: reportId ?? "",
                    documentTitle: row.title,
                  }))
                }
                label={deleteDocument ? "Delete" : "Remove"}
                icon={deleteDocument ? <Delete /> : <Close />}
              />
            </Tooltip>,
          ];
        },
      },
    ],
    [
      isReadOnly,
      deleteDocument,
      fetchAndHandleDownload,
      additionalAddEditState,
      removeDialogState.message,
      reportId,
      setDocumentsToReview,
      hasDocumentAI,
    ]
  );

  const documentType = addEditDialogState.documentType;
  const hmbpSection = documentType ? hmbpSectionFromDocumentType[documentType] : undefined;

  return (
    <>
      <Stack display="flex" flexDirection="column" flexGrow={1}>
        <Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="end"
            gap={theme.spacing(2)}
            mb={theme.spacing(1)}
          >
            <Box>
              {!loading &&
                !isReadOnly &&
                rows.length === 0 &&
                facilityDocumentIssues.length === 0 && (
                  <Alert severity="info" sx={{ mt: theme.spacing(1), fontSize: "0.7rem" }}>
                    Your state does not require this facility to provide any additional
                    documentation, however you can voluntarily add documentation like emergency
                    response or site plans here.
                  </Alert>
                )}
            </Box>

            <Stack direction="row" spacing={2}>
              {hasDocumentAI && selectedRows.ids.size > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => {
                    setDocumentsToReview(Array.from(selectedRows.ids) as string[]);
                  }}
                >
                  Edit Details ({selectedRows.ids.size})
                </Button>
              )}
              <CommandButton
                variant="contained"
                sx={{
                  visibility: isReadOnly ? "hidden" : "visible",
                  minWidth: "auto",
                }}
                onClick={() =>
                  setAddEditDialogState((state) => ({
                    ...state,
                    open: true,
                    mode: reportId ? EditMode.ADD_OR_PICK : EditMode.ADD,
                    ...additionalAddEditState,
                  }))
                }
              >
                Add Document
              </CommandButton>
            </Stack>
          </Stack>

          <DataGrid<DataGridRow>
            columns={columns}
            rows={rows}
            rowCount={count}
            autoHeight={true}
            loading={loading}
            rowSelection={hasDocumentAI && !isReadOnly}
            checkboxSelection={hasDocumentAI && !isReadOnly}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
            rowSelectionModel={selectedRows}
            noRowsMessage="No documents have been added yet."
            paginationMode="server"
            sortingMode="server"
            hideFooter={paginationModel === undefined ? true : undefined}
            pagination={!!paginationModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            onRowClick={(params: GridRowParams<DataGridRow>) => {
              if (isDocumentIssueRow(params.row)) {
                const { metadata } = params.row;
                setAddEditDialogState((state) => ({
                  ...state,
                  open: true,
                  mode: EditMode.ADD_OR_PICK,
                  documentType: metadata?.documentType,
                  currentSubmissionDocument: true,
                  isAddingFromIssue: true,
                  ...additionalAddEditState,
                }));
              } else {
                if (!hasDocumentAI) {
                  // for non ai customers, use the add edit dialog
                  setAddEditDialogState((state) => ({
                    ...state,
                    ...params.row,
                    open: true,
                    mode: EditMode.EDIT,
                    step: FormState.FORM,
                    documentId: params.row.id,
                    currentSubmissionDocument: params.row.currentSubmissionDocument ?? false,
                    ...additionalAddEditState,
                  }));
                } else {
                  // Use ReviewDocumentMetadataDialog for editing
                  setDocumentsToReview([params.row.id]);
                }
              }
            }}
          />
        </Stack>
      </Stack>

      {isHmbp && hmbpSection && addEditDialogState.isAddingFromIssue && tenantId ? (
        <HmbpDocumentForm
          section={hmbpSection}
          handleClose={() => setAddEditDialogState(defaultAddEditDocumentDialogState)}
        />
      ) : (
        tenantId && (
          <DocumentDialog
            tenantId={tenantId}
            reviewDialogProps={{
              open: documentsToReview.length > 0,
              onClose: () => {
                clearDocumentsToReview();
                setSelectedRows({ type: "include", ids: new Set() });
              },
              documentIds: documentsToReview,
              onComplete: () => {
                apolloClient.refetchQueries({
                  include: [...refetchQueries],
                });
              },
            }}
            addEditDialogProps={{
              dialogState: addEditDialogState,
              setDialogState: setAddEditDialogState,
              reportId: reportId,
              reportingYear: report?.data?.tierIIReport?.reportingYear,
              state:
                fullFacility?.data?.facility?.state ?? report?.data?.tierIIReport?.facility?.state,
              documentTypeOptions: selectableDocumentTypes,
              onSubmit: () => {
                apolloClient.refetchQueries({
                  include: [...refetchQueries],
                });
              },
              refetchQueries: refetchQueries,
              facilityDocumentIssues: facilityDocumentIssues,
              isReadOnly: isReadOnly,
            }}
          />
        )
      )}

      {removeDialogState.open && (
        <ConfirmDialog
          open={removeDialogState.open}
          header={
            deleteDocument
              ? "Are you sure you want to delete this document?"
              : "Are you sure you want to remove this document from the report?"
          }
          info={removeDialogState.message}
          resources={[
            {
              resourceType: "Document",
              resourceName: removeDialogState?.documentTitle,
            },
          ]}
          confirmText={deleteDocument ? "Yes, Delete" : "Yes, Remove"}
          loading={deletingDocument || removingDocument}
          onClose={() => setRemoveDialogState(defaultRemoveDialogState)}
          onConfirm={async () => {
            if (deleteDocument) {
              await deleteDocumentMutation({
                variables: {
                  id: removeDialogState.documentIdToRemove,
                },
                refetchQueries,
              });
            } else {
              await removeDocumentFromReport({
                variables: {
                  documentId: removeDialogState.documentIdToRemove,
                  reportId: removeDialogState.reportId,
                },
                refetchQueries,
              });
            }
            setRemoveDialogState(defaultRemoveDialogState);
            if (deleteDocument) {
              alerts.success("Document successfully deleted");
            } else {
              alerts.success("Document successfully removed");
            }
          }}
        />
      )}
    </>
  );
};
